import PropTypes from 'prop-types';
import { kebabCase } from 'lodash';
import classnames from 'classnames';
import React from 'react';
import { conformanceSelector, enabledSelector, optionsSelector, valueSelector, labelSelector, newPathSelector } from '../selectors/index';
import ConformanceTooltip from './conformanceTooltip';
import entityComposition from '../../common/classes/entityComposition';
import Label from './label';
import OptionMultiPicker from './optionMultiPicker';
import OptionSinglePicker from './optionSinglePicker';
import OptionNestedPicker from './optionNestedPicker/optionNestedPicker';
import OptionsTablePicker from './optionsTablePicker';
import FormSearchField from './formSearchField';
import logValueOnChange from '../commonActions/logValueOnChange';
const OptionPickerField = ({
  allowsClear: _allowsClear = true,
  allowsMultipleSelection: _allowsMultipleSelection = false,
  allowsOther: _allowsOther = false,
  allowsParentSelection: _allowsParentSelection = false,
  className: _className = '',
  conformanceText: _conformanceText = null,
  contentPath: _contentPath = undefined,
  enabled,
  format: _format = {},
  label: _label = null,
  labelKey: _labelKey = 'title',
  maxTextLength: _maxTextLength = 2024,
  onChange,
  onFocus: _onFocus = () => null,
  options: _options = [],
  optionsTable: _optionsTable = null,
  parentContentPath: _parentContentPath = undefined,
  path,
  placeholder: _placeholder = '',
  testingIdentifier: _testingIdentifier = '',
  type: pickerType = undefined,
  value: _value = '',
  valueKey: _valueKey = 'identifier',
  variables: _variables = {}
}) => {
  const showConformance = !!_conformanceText;
  let newClassName = _className;
  if (_label && _label.length) {
    newClassName = `t-opf t-opf-${kebabCase(_label)} ${_className}`;
  }
  if (showConformance) {
    newClassName += ' is-invalid';
  }
  const id = `opf-${kebabCase(_label)}`;
  const displayPlaceholder = _placeholder.length ? _placeholder : 'Select...';

  // warning: in the sub components we're using destructuring (e.g., {...props}) which is hiding
  // prop drilling. We probably should refactor this hierarchy to use context to get settings to
  // the eventual Select component
  const newProps = {
    allowsClear: _allowsClear,
    allowsMultipleSelection: _allowsMultipleSelection,
    allowsOther: _allowsOther,
    allowsParentSelection: _allowsParentSelection,
    className: newClassName,
    enabled,
    format: _format,
    id,
    labelKey: _labelKey,
    maxTextLength: _maxTextLength,
    onFocus: _onFocus,
    onChange,
    options: _options,
    optionsTable: _optionsTable,
    path,
    parentContentPath: _parentContentPath,
    pickerType,
    placeholder: displayPlaceholder,
    testingIdentifier: _testingIdentifier,
    value: _value,
    valueKey: _valueKey,
    variables: _variables,
    verticalPadding: false,
    contentPath: _contentPath
  };
  if (_allowsMultipleSelection) {
    // Modify props to pass in an array of values
    newProps.values = _value === '' ? [] : _value;
  }
  const valueIsATree = Object.values(_value || {}).some(val => val.children !== undefined);
  const isTieredPicker = _options.some(option => option.options !== undefined) || valueIsATree;
  const isTablePicker = _options && _options.length === 0 && !!_optionsTable;
  let Picker;
  if (pickerType === 'formSearchField' || pickerType === 'optionSearchField') {
    Picker = FormSearchField;
  } else if (isTablePicker) {
    Picker = OptionsTablePicker;
  } else if (isTieredPicker) {
    Picker = OptionNestedPicker;
  } else if (_allowsMultipleSelection) {
    Picker = OptionMultiPicker;
  } else {
    Picker = OptionSinglePicker;
  }

  // These pickers open a modal which needs a title, we use the label
  // text for this
  if (isTablePicker || isTieredPicker) {
    newProps.label = _label;
  }
  const wrapperClassName = classnames('t-opf', {
    [`t-opf-${kebabCase(_label)}`]: !!(_label && _label.length),
    'is-invalid': showConformance
  });
  return /*#__PURE__*/React.createElement("div", {
    id: id,
    "data-testid": id,
    className: wrapperClassName
  }, /*#__PURE__*/React.createElement(Label, {
    value: _label
  }), /*#__PURE__*/React.createElement(Picker, newProps), /*#__PURE__*/React.createElement(ConformanceTooltip, {
    errorText: _conformanceText,
    target: id,
    placement: "right"
  }));
};
OptionPickerField.propTypes = {
  allowsClear: PropTypes.bool,
  allowsMultipleSelection: PropTypes.bool,
  allowsOther: PropTypes.bool,
  allowsParentSelection: PropTypes.bool,
  // defaults are assigned at a lower level
  className: PropTypes.string,
  contentPath: PropTypes.string,
  conformanceText: PropTypes.string,
  enabled: PropTypes.bool.isRequired,
  format: PropTypes.shape({}),
  label: PropTypes.string,
  labelKey: PropTypes.string,
  maxTextLength: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  onFocus: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.shape({})),
  optionsTable: PropTypes.string,
  parentContentPath: PropTypes.string,
  path: PropTypes.string.isRequired,
  placeholder: PropTypes.string,
  testingIdentifier: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.arrayOf(PropTypes.shape({})), PropTypes.shape({}), PropTypes.string]),
  valueKey: PropTypes.string,
  variables: PropTypes.shape({})
};
export { OptionPickerField as UncomposedOptionPickerField };
export default entityComposition(OptionPickerField, [], {
  conformanceText: conformanceSelector,
  enabled: enabledSelector,
  label: labelSelector,
  options: optionsSelector,
  value: valueSelector,
  onChange: logValueOnChange,
  path: newPathSelector
});